/**
 * Nav app `ha.dashboard.navigation` — sidebar du Command Center HorApp.
 *
 * Responsabilités côté front (au-delà du rendu DOM porté par Navigation parent) :
 *  - Active item dérivé de `window.location.pathname` (la sidebar reflète la
 *    page en cours sans dépendre d'un état serveur).
 *  - Toggle des submenus (`.has-submenu` + `.toggle-submenu`) avec persistance
 *    de l'état open/close par `data-nav-key` dans localStorage.
 *  - Toggle global du sidebar (`#sidebar-toggle` → classe `.collapsed` sur la
 *    sidebar + `.nav-collapsed` sur <body>), persisté lui aussi.
 *
 * À terme cette nav porte aussi les assets dashboard (admin.css, line-awesome)
 * pour éviter de les recharger par page — pas dans cette itération.
 */
import { Navigation } from "./Navigation";
import { ControllerRegistry } from "../../Registry/ControllerRegistry";
import { toMap, timeout, getAppService } from "../../../../HA";

const LS_SUBMENU_KEY = "ha.dashboard.nav.submenu";
const LS_COLLAPSED_KEY = "ha.dashboard.nav.collapsed";

export class DashboardNavigation extends Navigation {
    protected initialize(data: Map<any, any>): void {
        super.initialize(data);
        // Le DOM peut ne pas être attaché immédiatement (la nav app est rendue
        // dans un Apps container). On diffère légèrement pour laisser le tag
        // s'attacher au document.
        timeout(() => this.bindDom(), 0);
    }

    private bindDom(): void {
        // Bind document-level (capture phase) : marche peu importe quel élément
        // du DOM est attaché. La sidebar du controller (tag créé en mémoire)
        // peut différer de la sidebar visible (rendue par NavigationAppManager
        // via App.getTag()) — un listener par instance binderait sur les
        // mauvais éléments. Le document-level résout ça.
        DashboardNavigation.bindGlobalListeners();
        const el = this.getRootElement();
        if (!el) {
            // Pas encore dans le DOM : retry pour les transformations d'état
            // initiales (active, submenu, collapsed).
            timeout(() => this.bindDom(), 100);
            return;
        }
        this.applyActiveFromUrl(el);
        this.restoreSubmenuState(el);
        this.restoreCollapsedState(el);
    }

    /** Listeners document globaux — installés une seule fois, capture phase. */
    private static globalListenersBound = false;
    private static bindGlobalListeners(): void {
        if (DashboardNavigation.globalListenersBound) { return; }
        DashboardNavigation.globalListenersBound = true;

        // Click sur a[data-app] DANS la sidebar dashboard : dispatch showApp.
        // Anchor.ts ne pose plus son onClick quand data-app/data-ha-set sont
        // présents (soft-handoff), donc on peut rester en bubble phase normale.
        // Scope strict #main-sidebar : on ne détourne pas les `a[data-app]` qui
        // existeraient ailleurs dans la SPA.
        document.addEventListener("click", (e: Event) => {
            const target = e.target as HTMLElement;
            if (!target || !target.closest) { return; }
            const link = target.closest("a[data-app]") as HTMLAnchorElement | null;
            if (!link) { return; }
            if (!link.closest("#main-sidebar")) { return; }
            const me = e as MouseEvent;
            if (me.metaKey || me.ctrlKey || me.shiftKey || me.button !== 0) { return; }
            e.preventDefault();
            const pageId = link.getAttribute("data-app") || "";
            if (!pageId) { return; }
            const svc = getAppService();
            if (!svc) {
                const href = link.getAttribute("href");
                if (href) { window.location.href = href; }
                return;
            }
            svc.processAction(toMap({t: "showApp", id: pageId}) as Map<any, any>);
        });

        // Click sur .toggle-submenu : open/close le .nav-item parent.
        document.addEventListener("click", (e: Event) => {
            const target = e.target as HTMLElement;
            if (!target || !target.closest) { return; }
            const toggle = target.closest(".toggle-submenu") as HTMLElement | null;
            if (!toggle) { return; }
            const sidebar = toggle.closest("#main-sidebar");
            if (!sidebar) { return; }
            e.preventDefault();
            const item = toggle.closest(".nav-item") as HTMLElement | null;
            if (!item) { return; }
            item.classList.toggle("open");
            DashboardNavigation.persistSubmenuState(sidebar as HTMLElement);
        });

        // Click sur #sidebar-toggle : collapse/expand global.
        document.addEventListener("click", (e: Event) => {
            const target = e.target as HTMLElement;
            if (!target || !target.closest) { return; }
            const btn = target.closest("#sidebar-toggle");
            if (!btn) { return; }
            e.preventDefault();
            const sidebar = document.getElementById("main-sidebar");
            if (!sidebar) { return; }
            sidebar.classList.toggle("collapsed");
            document.body.classList.toggle("nav-collapsed");
            try {
                localStorage.setItem(LS_COLLAPSED_KEY,
                    sidebar.classList.contains("collapsed") ? "1" : "0");
            } catch (err) { /* localStorage indisponible */ }
        });
    }

    private static persistSubmenuState(sidebar: HTMLElement): void {
        const openKeys: string[] = [];
        const items = sidebar.querySelectorAll(".has-submenu");
        items.forEach((item: Element) => {
            const key = item.getAttribute("data-nav-key");
            if (key && item.classList.contains("open")) {
                openKeys.push(key);
            }
        });
        try { localStorage.setItem(LS_SUBMENU_KEY, JSON.stringify(openKeys)); }
        catch (e) { /* ignore */ }
    }

    /** Resolve the <aside id="main-sidebar"> root element. */
    private getRootElement(): HTMLElement | null {
        const tag = this.getTag();
        if (tag && tag.getElement) {
            const el = tag.getElement() as HTMLElement;
            if (el) { return el; }
        }
        return document.getElementById("main-sidebar");
    }

    /**
     * Mark the <li> whose <a href> matches the current pathname as `.active`,
     * and `.open` its ancestor `.has-submenu` if any.
     */
    private applyActiveFromUrl(root: HTMLElement): void {
        const here = window.location.pathname;
        const links = root.querySelectorAll("a[href]");
        for (let i = 0; i < links.length; i++) {
            const a = links[i] as HTMLAnchorElement;
            const href = a.getAttribute("href") || "";
            if (!href || href === "#") { continue; }
            // Compare on the path portion only (ignore query/hash).
            let linkPath = href;
            try { linkPath = new URL(href, window.location.origin).pathname; }
            catch (e) { /* keep raw */ }
            if (linkPath !== here) { continue; }
            const li = a.closest("li");
            if (!li) { continue; }
            li.classList.add("active");
            const parentItem = li.closest("li.has-submenu") as HTMLElement | null;
            if (parentItem && parentItem !== li) {
                parentItem.classList.add("open", "active");
            }
        }
    }

    private restoreSubmenuState(root: HTMLElement): void {
        let openKeys: string[] = [];
        try {
            const raw = localStorage.getItem(LS_SUBMENU_KEY);
            if (raw) { openKeys = JSON.parse(raw) || []; }
        } catch (e) { return; }
        if (!Array.isArray(openKeys)) { return; }
        openKeys.forEach((key: string) => {
            const item = root.querySelector(`.has-submenu[data-nav-key="${key}"]`);
            if (item) { item.classList.add("open"); }
        });
    }

    private restoreCollapsedState(root: HTMLElement): void {
        try {
            if (localStorage.getItem(LS_COLLAPSED_KEY) === "1") {
                root.classList.add("collapsed");
                document.body.classList.add("nav-collapsed");
            }
        } catch (e) { /* ignore */ }
    }
}

ControllerRegistry.register("DashboardNavigation", DashboardNavigation);
